import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { fetchTransactionDetailsThunk, updateBusinessStatus } from "../../../services/MasterApi";

export default function Payment() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const token = sessionStorage.getItem("bb_token");
  const transactionId = sessionStorage.getItem("bb_transactionId");

  const [paymentNote, setPaymentNote] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (transactionId && token) {
      dispatch(fetchTransactionDetailsThunk({ token, transactionId }));
    }
  }, [dispatch, token, transactionId]);

  const transactionData = useSelector((state) => state.transactionDetails);
  const { data: businessData, isLoading } = transactionData;

  // Compute real items
  const items = [];
  let subtotalCalc = 0;

  if (businessData?.packages?.name) {
    const cost = parseFloat(businessData.packages.price || 0);
    subtotalCalc += cost;
    items.push({ name: `${businessData.packages.name} Formation Tier`, cost });
  } else {
    items.push({ name: "Entity Formation Filing Service", cost: 0.0 });
  }

  if (businessData?.operationAgreement?.name) {
    const cost = parseFloat(businessData.operationAgreement.price || 0);
    subtotalCalc += cost;
    items.push({ name: businessData.operationAgreement.name, cost });
  }

  if (businessData?.expeditedProcess?.name) {
    const cost = parseFloat(businessData.expeditedProcess.price || 0);
    subtotalCalc += cost;
    items.push({ name: businessData.expeditedProcess.name, cost });
  }

  if (businessData?.foundersKit?.name) {
    const cost = parseFloat(businessData.foundersKit.price || 0);
    subtotalCalc += cost;
    items.push({ name: businessData.foundersKit.name, cost });
  }

  const filingFee =
    businessData?.businessPayment?.filingFee != null
      ? parseFloat(businessData.businessPayment.filingFee)
      : 100.0;

  const total =
    businessData?.businessPayment?.totalPrice != null
      ? parseFloat(businessData.businessPayment.totalPrice)
      : subtotalCalc + filingFee;

  const isPaid = Boolean(businessData?.businessPayment?.paymentTransactionId);

  const handleMarkPaymentApproved = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    if (!transactionId) {
      setErrorMsg("No active transaction found to update.");
      return;
    }

    try {
      setIsProcessing(true);
      // Advance status to Approved / Completed (status ID 3)
      await updateBusinessStatus(transactionId, 3);
      setSuccessMsg("Payment verified and application status updated to Completed.");
      setTimeout(() => {
        navigate(`/details/${transactionId}`);
      }, 1200);
    } catch (err) {
      setErrorMsg(err.message || "Failed to update payment status.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-72px)] bg-[#f8fafc] py-6 px-4 sm:px-8 font-sans">
      <div className="w-full space-y-5">
        {/* Back Link */}
        <div className="flex items-center justify-between">
          <Link
            to={transactionId ? `/details/${transactionId}` : "/table"}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-white hover:bg-slate-50 border border-slate-200/90 rounded-lg text-slate-600 hover:text-[#1a73e8] text-[13px] font-medium transition-all shadow-2xs"
          >
            <i className="fa-solid fa-arrow-left text-xs"></i>
            <span>Back to Application Details</span>
          </Link>
        </div>

        {/* Page Header */}
        <div className="bg-white rounded-xl border border-slate-200/90 p-4 sm:p-5 shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="bg-slate-100 text-slate-700 font-semibold text-[11px] px-2.5 py-0.5 rounded-md">
                #REGD{businessData?.businessTransactionId || transactionId || "N/A"}
              </span>
              <span className="bg-blue-50 text-[#1a73e8] font-semibold text-[11px] px-2.5 py-0.5 rounded-md">
                {businessData?.businessType?.name || "Business Formation"}
              </span>
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              Review Order & Payment Details
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Live registration fee breakdown and payment verification for{" "}
              <strong>{businessData?.businessName || "Application"}</strong>
            </p>
          </div>
          <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200/80 px-3 py-1.5 rounded-lg text-emerald-700 text-xs font-semibold self-start sm:self-auto">
            <i className="fa-solid fa-shield-halved text-sm text-emerald-600"></i>
            <span>256-Bit SSL Encrypted</span>
          </div>
        </div>

        {/* Feedback alerts */}
        {errorMsg && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-xs font-semibold p-3 rounded-xl flex items-center gap-2">
            <i className="fa-solid fa-circle-exclamation text-sm"></i>
            <span>{errorMsg}</span>
          </div>
        )}

        {successMsg && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold p-3 rounded-xl flex items-center gap-2">
            <i className="fa-solid fa-circle-check text-sm"></i>
            <span>{successMsg}</span>
          </div>
        )}

        {/* 2-Column Responsive Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
          {/* Left Column: Order Summary (6 cols) */}
          <div className="lg:col-span-6 space-y-4">
            <div className="bg-white rounded-xl border border-slate-200/90 p-5 shadow-2xs">
              <div className="flex items-center gap-2.5 pb-3.5 mb-4 border-b border-slate-100">
                <div className="w-8 h-8 rounded-lg bg-blue-50 text-[#1a73e8] flex items-center justify-center">
                  <i className="fa-solid fa-receipt text-sm"></i>
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-slate-900 leading-tight">
                    Order Summary
                  </h2>
                  <p className="text-[11px] text-slate-500 mt-0.5">Live itemized statement of statutory fees</p>
                </div>
              </div>

              {/* Items List */}
              <div className="space-y-2.5 mb-4 text-[13px]">
                {items.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center py-1.5 border-b border-slate-50">
                    <span className="text-slate-600 font-medium">{item.name}</span>
                    <span className="font-semibold text-slate-900">
                      {item.cost === 0 ? "Included" : `$${item.cost.toFixed(2)}`}
                    </span>
                  </div>
                ))}
                <div className="flex justify-between items-center py-1.5 border-b border-slate-50">
                  <span className="text-slate-600 font-medium">State Filing & Statutory Fee</span>
                  <span className="font-semibold text-slate-900">${filingFee.toFixed(2)}</span>
                </div>
              </div>

              {/* Total Block */}
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-200/80 space-y-1.5">
                <div className="flex justify-between text-xs text-slate-500">
                  <span>Subtotal</span>
                  <span className="font-semibold text-slate-700">${subtotalCalc.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs text-slate-500">
                  <span>State Statutory Fee</span>
                  <span className="font-semibold text-slate-700">${filingFee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-base font-bold text-slate-900 pt-2 border-t border-slate-200">
                  <span>Total Invoiced</span>
                  <span className="text-[#1a73e8]">${total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Payment Status & Admin Verification (6 cols) */}
          <div className="lg:col-span-6 space-y-4">
            <div className="bg-white rounded-xl border border-slate-200/90 p-5 shadow-2xs">
              <div className="flex items-center gap-2.5 pb-3.5 mb-4 border-b border-slate-100">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                  <i className="fa-solid fa-credit-card text-sm"></i>
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-slate-900 leading-tight">
                    Payment Status & Verification
                  </h2>
                  <p className="text-[11px] text-slate-500 mt-0.5">Gateway reference & settlement records</p>
                </div>
              </div>

              {isPaid ? (
                <div className="space-y-4">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                    <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-2 text-lg">
                      <i className="fa-solid fa-check"></i>
                    </div>
                    <h3 className="text-sm font-bold text-emerald-900">
                      Payment Successfully Processed
                    </h3>
                    <p className="text-xs text-emerald-700 mt-0.5 font-mono">
                      Stripe Ref: {businessData.businessPayment.paymentTransactionId}
                    </p>
                  </div>

                  <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 text-xs space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Payer Name:</span>
                      <span className="font-semibold text-slate-800">
                        {businessData.businessPayment.payer?.name || businessData.primaryContact?.fullName || "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Payer Email:</span>
                      <span className="font-semibold text-slate-800">
                        {businessData.businessPayment.payer?.email || businessData.primaryContact?.email || "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Payment Mode:</span>
                      <span className="font-semibold text-slate-800">
                        {businessData.businessPayment.paymentMode || "Stripe (Card)"}
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => navigate(`/details/${transactionId}`)}
                    className="w-full py-2.5 bg-[#1a73e8] hover:bg-[#1557b0] text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer"
                  >
                    View Application Details
                  </button>
                </div>
              ) : (
                <form onSubmit={handleMarkPaymentApproved} className="space-y-4">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-start gap-2.5">
                    <i className="fa-solid fa-circle-info text-amber-600 mt-0.5"></i>
                    <div>
                      <strong className="block font-semibold">Payment Unsettled</strong>
                      <span>
                        This application has not yet completed online Stripe checkout. As an admin, you can verify manual payment or mark this application completed.
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Administrative Note (Optional)
                    </label>
                    <textarea
                      rows={3}
                      className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#1a73e8]/20 focus:border-[#1a73e8]"
                      placeholder="e.g. Verified check / wire payment / authorized manually..."
                      value={paymentNote}
                      onChange={(e) => setPaymentNote(e.target.value)}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isProcessing}
                    className="w-full py-2.5 bg-[#1a73e8] hover:bg-[#1557b0] text-white text-xs font-semibold rounded-lg shadow-sm transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
                  >
                    {isProcessing ? (
                      <>
                        <i className="fa-solid fa-spinner animate-spin"></i>
                        <span>Updating Record...</span>
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-check-double"></i>
                        <span>Verify & Mark Application Completed</span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
